import { invalidate, ThreeEvent, useThree } from '@react-three/fiber';
import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { type BatchedMesh } from 'three';

import { updateAttributeRange } from 'app/modules/wallforming/components/scenes/objects/articles/utils/batchedMeshUtils';
import { usePoiSelectionInfo } from 'app/modules/wallforming/components/scenes/objects/pois/hooks/usePoiSelectionInfo';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { PoiNavigationSource } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiNavigationSource';
import { isCornerSingleSidePoi } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { PoiBatchedEntry } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiBatchedEntry';

const updatedIndices = new Set<number>();
const DOUBLE_CLICK_DELAY_MS = 200;

interface PoiEventHandlerProps {
    entries: PoiBatchedEntry[];
    meshRef: RefObject<BatchedMesh | null>;
    hoveredBatchIdRef: RefObject<number | null>;
    instanceIdByEntryIndexRef: RefObject<number[]>;
    entryIndexByInstanceIdRef: RefObject<Map<number, number>>;
}

export const usePoiEventHandler = ({
    entries,
    meshRef,
    hoveredBatchIdRef,
    instanceIdByEntryIndexRef,
    entryIndexByInstanceIdRef
}: PoiEventHandlerProps) => {
    const { prevFilterTypeRef, prevIndexRef, selectedPoiIndexRef } = usePoiSelectionInfo();

    const gl = useThree((state) => state.gl);
    const clickCountRef = useRef(0);
    const clickTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const pendingClickDataRef = useRef<{
        batchId: number;
        entry: PoiBatchedEntry;
        previousPoiIndex: number | undefined;
    } | null>(null);

    const entryIndexByPoiIndex = useMemo(
        () => new Map(entries.map((entry, entryIndex) => [entry.poiIndex, entryIndex])),
        [entries]
    );

    const resolveBatchEvent = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            const batchId = event.batchId;
            if (batchId === undefined) {
                return null;
            }

            const entryIndex = entryIndexByInstanceIdRef.current.get(batchId) ?? batchId;
            const entry = entries[entryIndex];
            if (!entry) {
                return null;
            }

            return { batchId, entry, entryIndex };
        },
        [entries, entryIndexByInstanceIdRef]
    );

    const handleSelection = useCallback(
        (
            batchId: number,
            entry: PoiBatchedEntry,
            mesh: BatchedMesh,
            previousPoiIndex: number | undefined,
            isDoubleClick: boolean
        ) => {
            const selectionChanged = prevIndexRef.current !== entry.poiIndex;
            if (selectionChanged) {
                usePlacementStore
                    .getState()
                    .setDisableCameraAlign(PoiNavigationSource.PointerSelection, !isDoubleClick);
            }

            const state = usePlacementStore.getState();

            if (isDoubleClick && entry.filterType === POIFilterType.Wall) {
                if (
                    state.mainCameraControlsRef.current &&
                    state.mainWebGLRendererRef.current &&
                    state.faceSelectionRef.current
                ) {
                    state.setFocusOnWall(
                        state.mainCameraControlsRef,
                        state.faceSelectionRef.current!,
                        state.mainWebGLRendererRef.current!.domElement
                    );
                }
            } else if (isDoubleClick && entry.filterType === POIFilterType.Corner) {
                const cornerPoi = state.pois[batchId]?.poi;
                if (cornerPoi && isCornerSingleSidePoi(cornerPoi)) {
                    state.setFocusOnCorner(
                        state.mainCameraControlsRef,
                        cornerPoi,
                        state.mainWebGLRendererRef.current?.domElement ?? null
                    );
                }
            }

            state.setPoiSelection(entry.filterType, entry.poiIndex, PoiNavigationSource.PointerSelection);

            prevFilterTypeRef.current = entry.filterType;
            prevIndexRef.current = entry.poiIndex;
            updatedIndices.clear();

            const previousEntryIndex = entryIndexByPoiIndex.get(previousPoiIndex ?? -1);
            const previousBatchId =
                previousEntryIndex !== undefined ? instanceIdByEntryIndexRef.current[previousEntryIndex] : undefined;
            if (previousBatchId !== undefined && previousBatchId !== batchId) {
                const previousEntry = previousEntryIndex !== undefined ? entries[previousEntryIndex] : undefined;
                if (previousEntry) {
                    mesh.setColorAt(previousBatchId, previousEntry.defaultColor);
                    updatedIndices.add(previousBatchId);
                }
            }

            mesh.setColorAt(batchId, entry.selectedColor);
            updatedIndices.add(batchId);
            updateAttributeRange(mesh, updatedIndices, 'colorAttribute', 4);

            hoveredBatchIdRef.current = batchId;
            invalidate();

            if (!usePlacementStore.getState().isSpaceKeyPressed) {
                requestAnimationFrame(() => {
                    usePlacementStore.getState().setInteractionMode(InteractionMode.DEFAULT);
                });
            }
        },
        [entries, entryIndexByPoiIndex, hoveredBatchIdRef, instanceIdByEntryIndexRef, prevFilterTypeRef, prevIndexRef]
    );

    const handleOnPoiSelection = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            if (event.button !== 0) {
                return;
            }

            const resolved = resolveBatchEvent(event);
            const mesh = meshRef.current;
            if (!resolved || !mesh) {
                return;
            }

            const { batchId, entry } = resolved;
            clickCountRef.current++;

            if (clickCountRef.current === 2) {
                clearTimeout(clickTimerRef.current);
                clickCountRef.current = 0;
                pendingClickDataRef.current = null;
                handleSelection(batchId, entry, mesh, prevIndexRef.current, true);
            } else {
                pendingClickDataRef.current = { batchId, entry, previousPoiIndex: prevIndexRef.current };
                clickTimerRef.current = setTimeout(() => {
                    clickCountRef.current = 0;
                    const pending = pendingClickDataRef.current;
                    pendingClickDataRef.current = null;
                    if (pending && meshRef.current) {
                        handleSelection(
                            pending.batchId,
                            pending.entry,
                            meshRef.current,
                            pending.previousPoiIndex,
                            false
                        );
                    }
                }, DOUBLE_CLICK_DELAY_MS);
            }

            event.stopPropagation();
        },
        [handleSelection, meshRef, prevIndexRef, resolveBatchEvent]
    );

    useEffect(() => {
        return () => clearTimeout(clickTimerRef.current);
    }, []);

    const handleOnPointerMove = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            if (event.buttons !== 0) {
                return;
            }

            const resolved = resolveBatchEvent(event);
            const mesh = meshRef.current;
            if (!resolved || !mesh) {
                return;
            }

            const { batchId, entry } = resolved;
            if (hoveredBatchIdRef.current === batchId) {
                return;
            }

            gl.domElement.style.setProperty('cursor', 'pointer');
            updatedIndices.clear();

            const previousBatchId = hoveredBatchIdRef.current;
            if (previousBatchId !== null) {
                const previousEntryIndex = entryIndexByInstanceIdRef.current.get(previousBatchId);
                const previousEntry = previousEntryIndex !== undefined ? entries[previousEntryIndex] : undefined;
                if (previousEntry && selectedPoiIndexRef.current !== previousEntry.poiIndex) {
                    mesh.setColorAt(previousBatchId, previousEntry.defaultColor);
                    updatedIndices.add(previousBatchId);
                }
            }

            if (selectedPoiIndexRef.current !== entry.poiIndex) {
                mesh.setColorAt(batchId, entry.hoverColor ?? entry.selectedColor);
                updatedIndices.add(batchId);
            }

            updateAttributeRange(mesh, updatedIndices, 'colorAttribute', 4);
            hoveredBatchIdRef.current = batchId;
            invalidate();
        },
        [
            entries,
            entryIndexByInstanceIdRef,
            gl.domElement.style,
            hoveredBatchIdRef,
            meshRef,
            resolveBatchEvent,
            selectedPoiIndexRef
        ]
    );

    const handleOnPointerLeave = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            if (event.buttons !== 0) {
                return;
            }

            gl.domElement.style.setProperty('cursor', 'auto');

            const previousBatchId = hoveredBatchIdRef.current;
            if (previousBatchId === null) {
                return;
            }

            const previousEntryIndex = entryIndexByInstanceIdRef.current.get(previousBatchId);
            const previousEntry = previousEntryIndex !== undefined ? entries[previousEntryIndex] : undefined;
            const mesh = meshRef.current;
            if (!mesh || !previousEntry) {
                return;
            }

            updatedIndices.clear();

            if (selectedPoiIndexRef.current !== previousEntry.poiIndex) {
                mesh.setColorAt(previousBatchId, previousEntry.defaultColor);
                updatedIndices.add(previousBatchId);
            }

            updateAttributeRange(mesh, updatedIndices, 'colorAttribute', 4);
            hoveredBatchIdRef.current = null;
            invalidate();
        },
        [entries, entryIndexByInstanceIdRef, gl.domElement.style, hoveredBatchIdRef, meshRef, selectedPoiIndexRef]
    );

    return {
        handleOnPoiSelection,
        handleOnPointerMove,
        handleOnPointerLeave
    };
};
